import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  doc,
  collection,
  getDoc,
  onSnapshot,
  setDoc,
  writeBatch,
  collectionGroup,
  query,
  where,
} from "firebase/firestore";
import { db } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import {
  blankPhases,
  blankBudgetCategories,
  blankContractors,
} from "@domain/project/blankTemplate";

export const ProjectContext = createContext(null);

// Composite key lets the user hold the same project id from two different
// owners active in parallel (e.g. "default" from self + "default" invited
// from a contractor's org). Format: `${ownerUid}|${id}`.
function projectKey(project) {
  return `${project.ownerUid}|${project.id}`;
}

function parseActiveKey(stored, fallbackOwnerUid) {
  if (!stored) return null;
  if (stored.includes("|")) {
    const [ownerUid, ...rest] = stored.split("|");
    return { ownerUid, id: rest.join("|") };
  }
  // Legacy format: stored as bare id — assume owned by current user.
  return { ownerUid: fallbackOwnerUid, id: stored };
}

export function ProjectProvider({ children }) {
  const { user } = useAuth();
  const [v2Projects, setV2Projects] = useState([]);
  const [activeKey, setActiveKeyLocal] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Subscribe to the projects the user is a member of. Collection-group
  // query on projects/*/members where subjectId matches the user's uid
  // or (pre-signup) lowercased email.
  useEffect(() => {
    if (!user) {
      setV2Projects([]);
      setLoaded(false);
      return undefined;
    }
    const subjects = [user.uid];
    if (user.email) subjects.push(user.email.toLowerCase());

    const q = query(
      collectionGroup(db, "members"),
      where("subjectId", "in", subjects)
    );

    const unsub = onSnapshot(
      q,
      async (snap) => {
        const projectRefs = snap.docs
          .map((d) => ({
            member: d.data(),
            projectRef: d.ref.parent.parent,
          }))
          .filter((entry) => {
            // Only interested in projects/{pid}/members/* matches.
            const path = entry.projectRef?.path ?? "";
            return path.startsWith("projects/");
          });

        try {
          const projectSnaps = await Promise.all(
            projectRefs.map((e) => getDoc(e.projectRef))
          );
          const list = projectSnaps
            .map((ps, i) => {
              if (!ps.exists()) return null;
              const pdata = ps.data();
              const entry = projectRefs[i];
              return {
                id: ps.id,
                name: pdata.name || "Project",
                address: pdata.address || "",
                createdAt: pdata.createdAt || 0,
                ownerUid: pdata.ownerId,
                ownerType: pdata.ownerType,
                ownerId: pdata.ownerId,
                ownerEmail: pdata.ownerEmail,
                createdBy: pdata.createdBy,
                shared: pdata.createdBy !== user.uid,
                role: entry.member.role || "editor",
                schema: "v2",
              };
            })
            .filter(Boolean);
          setV2Projects(list);
          setLoaded(true);
        } catch (err) {
          console.error("[useProjects] v2 hydrate error", err);
          setV2Projects([]);
          setLoaded(true);
        }
      },
      (err) => {
        console.error("[useProjects] v2 snapshot error", err);
        setLoaded(true);
      }
    );
    return unsub;
  }, [user]);

  // Subscribe to active project id at the v2 settings path.
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, "users", user.uid, "settings", "activeProject");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists() && snap.data().value) {
          setActiveKeyLocal(String(snap.data().value));
        }
      },
      (err) => {
        console.error("[useProjects] activeProject snapshot error", err);
      }
    );
    return unsub;
  }, [user]);

  const setActiveProject = useCallback(
    (project) => {
      if (!project) return;
      const key =
        typeof project === "string"
          ? project.includes("|")
            ? project
            : `${user?.uid}|${project}`
          : projectKey(project);
      setActiveKeyLocal(key);
      if (user) {
        const ref = doc(db, "users", user.uid, "settings", "activeProject");
        setDoc(ref, { value: key }, { merge: true }).catch((err) =>
          console.error("[useProjects] write activeProject failed", err)
        );
      }
    },
    [user]
  );

  /**
   * Create a new top-level project at projects/{pid}, plus an owner-member
   * doc for the current user, plus seed blank data docs (phases, budget,
   * contractors) in a single batched write. Sets the new project active.
   */
  const createProject = useCallback(
    async ({ name, address = "", sqFt = null }) => {
      if (!user) return null;
      const projectsCol = collection(db, "projects");
      const projectRef = doc(projectsCol);
      const id = projectRef.id;

      const projectData = {
        id,
        name: name || "My Renovation",
        address,
        createdAt: Date.now(),
        createdBy: user.uid,
        ownerType: "user",
        ownerId: user.uid,
        ownerEmail: user.email || "",
        ...(sqFt ? { sqFt } : {}),
      };

      const memberRef = doc(db, "projects", id, "members", `user_${user.uid}`);
      const memberData = {
        subjectType: "user",
        subjectId: user.uid,
        email: user.email || "",
        role: "owner",
        invitedBy: user.uid,
        invitedAt: Date.now(),
        joinedAt: Date.now(),
      };

      try {
        // Sequential: rules evaluate exists() against pre-write state, so
        // the member doc must be written after the project doc, and seed data
        // after the member doc (canEdit depends on member existing).
        await setDoc(projectRef, projectData);
        await setDoc(memberRef, memberData);
        await Promise.all([
          setDoc(doc(db, "projects", id, "data", "phases"), { value: blankPhases }, { merge: true }),
          setDoc(doc(db, "projects", id, "data", "budget-b"), { value: blankBudgetCategories }, { merge: true }),
          setDoc(doc(db, "projects", id, "data", "contractors"), { value: blankContractors }, { merge: true }),
        ]);
      } catch (err) {
        console.error("[useProjects] createProject failed", err);
        throw err;
      }

      const compositeKey = `${user.uid}|${id}`;
      setActiveKeyLocal(compositeKey);
      const activeRef = doc(db, "users", user.uid, "settings", "activeProject");
      setDoc(activeRef, { value: compositeKey }, { merge: true }).catch((err) =>
        console.error("[useProjects] write activeProject failed", err)
      );
      return id;
    },
    [user]
  );

  const projects = v2Projects;

  const parsed = parseActiveKey(activeKey, user?.uid);
  const activeProject = parsed
    ? projects.find(
        (p) => p.ownerUid === parsed.ownerUid && p.id === parsed.id
      ) || projects[0]
    : projects[0];

  // Back-compat: still expose activeProjectId for callers that read it.
  const activeProjectId = activeProject?.id || null;

  return (
    <ProjectContext.Provider
      value={{
        projects,
        activeProjectId,
        activeProject,
        setActiveProject,
        createProject,
        // Alias kept for the OnboardingWizard until its call site updates.
        createV2Project: createProject,
        loaded,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProjects() {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error("useProjects must be used within a ProjectProvider");
  }
  return ctx;
}
